//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState, useCallback, useEffect } from 'react';
import { ButtonBase, Grid, Menu, MenuItem, Popover } from '@material-ui/core';
import VerifiedUserRoundedIcon from '@material-ui/icons/VerifiedUserRounded';
import CloseIcon from '@material-ui/icons/Close';
import MenuIcon from '@material-ui/icons/Menu';
import BusinessIcon from '@material-ui/icons/Business';

import Drawer from '@material-ui/core/Drawer';
import CssBaseline from '@material-ui/core/CssBaseline';
import AppBar from '@material-ui/core/AppBar';

import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import {
    PATH_BRAND,
    PATH_DASHBOARD,
    PATH_HOME,
    PATH_SUB_PAGE_TERMS,
    PATH_SUB_PAGE_PRIVACY,
    PATH_SUB_PAGE_FAQ,
    PATH_JOB_CENTER,
    PATH_RESOURCE_CENTER,
    PATH_ADVERTISE,
    PATH_EMP_ACCOUNT_LINKAGE,
    PATH_SUB_PAGE_EMP,
    PATH_SURVEY,
    PATH_JOIN_INTERVIEW,
    PATH_JOIN_INTERVIEW_TEST_JOIN_CODE,
    PRE_LAUNCH_PAGE,
    PATH_ADMIN_CONSOLE,
} from '../../system/constants';
import {
    BASE_URL,
    DASHBOARD_ID_MY_SETTINGS,
    DASHBOARD_ID_MY_SETTINGS_USERS,
    HOMEPAGE_FAQ,
    HOMEPAGE_CONTACT,
    HOMEPAGE_EMPLOYERS,
    HOMEPAGE_ABOUT,
    DASHBOARD_ID_MY_CALENDAR,
    DASHBOARD_ID_MY_JOBS,
    DASHBOARD_ID_MY_MESSAGES,
    DASHBOARD_ID_MY_PROFILE,
    ADVERTISE_PAGE_ID_INTRO,
    // PENDING_ACCOUNT_SETUP,
    // ACCOUNT_STATUS_ACTIVE,
    PENDING_EMPLOYER_INVITATIONS,
    // ACCOUNT_STATUS_PENDING_MEMBER_SETUP,
    // ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP,
    // YOUR_COMPANY,
    ADMIN_ID_JOB_SEEKER,
    COMPANY,
    ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP,
    ADD_NEW_COMPANY,
    OTHER_LINKED_COMPANIES,
    ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION,
} from '../../common/constants';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { getFullNameMiddleInitialByUser } from '../../common/textHelper';
import { getUnreadMessageCountByDashboardData } from '../../common/dashboardHelper';
import { getIsAccountSwitchable } from '../../common/employerHelper';

// import List from '@material-ui/core/List';

// import brandLogo from '../../images/brandLogo.png';
// import instagram from '../../images/drawer/instagram.png';
// import facebook from '../../images/drawer/facebook.png';
// import pinterest from '../../images/drawer/pinterest.png';
// import twitter from '../../images/drawer/twitter.png';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
// import { isOnPage } from '../../system/helpers/systemHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import Logo from '../../system/Logo';
import LogoSm from '../../system/LogoSm';
import AfitdBox from '../../common/elemental/AfitdBox';
import AfitdAvatarWithRing from '../../common/elemental/AfitdAvatarWithRing';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdBadge from '../../common/elemental/AfitdBadge';
import AfitdButton from '../../common/elemental/AfitdButton';
// import AfitdCompanyName from '../../common/elemental/AfitdCompanyName';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';
import TopNavButton from './TopNavButton';
import TopNavEmpSmallMenu from './TopNavEmpSmallMenu';
import EmpDashboardPopover from './EmpDashboardPopover';
import ConfirmAddNewCompanyDialog from './ConfirmAddNewCompanyDialog';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _some from 'lodash/some';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const drawerWidth = '100%';
const useStyles = (params = {}) => {
    const { currentTier } = params;
    const nonFirstButtonContainerStyles = { marginLeft: '40px' };

    return makeStyles(theme => ({
        root: {
            display: 'flex',
        },
        topNavContainer: {
            overflow: 'hidden',
            overflowX: 'hidden',
            paddingRight: '64px',
            background: '#FAFAFC',
            width: '100%',
            height: '80px',
            borderBottom: '1px solid #F0F2F6',
            // [theme.breakpoints.down('sm')]: { paddingRight: 0, height: '136px' },
            [theme.breakpoints.down('md')]: { paddingRight: 50 },
            [theme.breakpoints.down('sm')]: { paddingRight: 0 },
        },
        topNavLeft: { display: 'flex', alignItems: 'center', [theme.breakpoints.up('md')]: { justifyContent: 'center' } },
        topNavRight: { height: '100%' },
        appLogoContainer: {
            // width: '368px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            [theme.breakpoints.up('xl')]: { width: '368px' },
            [theme.breakpoints.down('sm')]: { borderBottom: '1px solid #F0F2F6', height: 80 },
        },
        appLogoHomeLink: { marginTop: 10, [theme.breakpoints.down('md')]: { marginTop: 6 } },
        // appLogo: { width: 165 },
        appLogoClose: { width: 180 },
        logoDrawerCloseContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            alignContent: 'center',
            marginLeft: 58,
            [theme.breakpoints.down('sm')]: { marginLeft: 10 },
        },
        drawerIconContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: 12 },
        notificationBadgeContainerClose: {
            background: '#fc821f',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            lineHeight: '24px',
            marginLeft: '-36px',
            cursor: 'pointer',
        },
        notificationBadgeContainer: {
            background: '#fc821f',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            lineHeight: '24px',
            cursor: 'pointer',
            marginRight: 12,
        },
        notificationBadge: {
            fontSize: '13px',
            color: '#ffffff',
            fontWeight: 'bold',
        },
        logoDrawerContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            height: '100%',
            alignContent: 'center',
            marginLeft: 38,
            [theme.breakpoints.down('sm')]: { marginLeft: 15 },
        },

        topNavRightContainer: {
            background: '#FAFAFC',
            width: '100%',
            height: '80px',
            borderBottom: '1px solid #F0F2F6',
            display: 'flex',
            flexDirection: 'row',
            [theme.breakpoints.down('md')]: { justifyContent: 'center', alignItems: 'center' },
        },
        topNavBottomContainer: {
            background: '#FAFAFC',
            width: '100%',
            borderTop: '1px solid #F0F2F6',
            display: 'flex',
            flexDirection: 'row',
            [theme.breakpoints.down('sm')]: { justifyContent: 'center', alignItems: 'center' },
            [theme.breakpoints.up('md')]: { height: '40px' },
        },
        popOverNavContainer: { width: '100%', height: '80px', display: 'flex', justifyContent: 'center', alignItems: 'center' },
        // leftContainer: {
        //     width: '368px',
        //     display: 'flex',
        //     alignItems: 'center',
        //     justifyContent: 'center',
        // },
        // rightContainer: {
        //     display: 'flex',
        //     flexDirection: 'row',
        // },
        advertiseButtonContainer: {
            ...nonFirstButtonContainerStyles,
            [theme.breakpoints.down('md')]: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '0px', marginRight: 0 /* 25 */ },
            [theme.breakpoints.down('sm')]: { marginRight: 0 },
        },
        // dashboardButtonContainer: {
        //     ...nonFirstButtonContainerStyles,
        //     [theme.breakpoints.down('md')]: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '0px' },
        // },
        tempMenuButtonContainer: {
            ...nonFirstButtonContainerStyles,
            [theme.breakpoints.down('md')]: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '0px' },
        },
        brandButtonContainer: {
            ...nonFirstButtonContainerStyles,
            [theme.breakpoints.down('md')]: { display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '0px', marginRight: 25 },
        },

        avatarContainer: {
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginLeft: '40px',
            [theme.breakpoints.down('md')]: { marginLeft: '0px', marginRight: '15px' },
        },
        // appLogo: {
        //     width: 165,
        // },
        avatar: {
            cursor: 'pointer',
        },
        avatarPopover: {
            // cursor: 'pointer',
        },
        profilePopOverContainer: { width: 340, [theme.breakpoints.down('xs')]: { width: '280px' } },
        closeIconContainerProfile: { textAlign: 'right', marginTop: 20, marginRight: 20, color: 'rgba(17, 30, 20, 0.4)' },
        closeIconContent: { cursor: 'pointer' },
        profileImage: { textAlign: 'center', marginTop: '-26px', display: 'flex', justifyContent: 'center', alignItems: 'center' },
        popoverContents: { textAlign: 'center' },
        fullNameText: { fontSize: 20, fontWeight: 'bold', paddingTop: 10 },
        currentCompanyNameText: { fontSize: 16, fontWeight: 'bold', padding: '0 16px 16px 16px' },
        verifyIconContainer: { position: 'relative', top: 5, display: currentTier === 3 ? 'inline' : 'none' },
        verifyIcon: { color: '#1D8A8B' },
        isAdminText: { fontSize: 18, fontWeight: 'bold' },
        emailText: { fontSize: 16, paddingTop: 5, color: 'rgba(17, 30, 20, 0.6)' },
        manageAccountBtn: { marginTop: 10 },
        logOutBtn: { marginTop: 15, marginBottom: 15 },

        privacyAndTermsText: { color: 'rgba(17, 30, 20, 0.6)', marginBottom: 20 },
        termsOfUseLink: {
            cursor: 'pointer',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        privacyLink: {
            cursor: 'pointer',
            '&:hover': {
                textDecoration: 'underline',
            },
        },
        menuBarIcon: {
            // marginRight: 19,
            color: 'rgba(17, 30, 20, 1)',
            cursor: 'pointer',
        },
        //drawer section style
        drawerListContainer: {
            paddingLeft: 24,
        },
        drawerList1: { fontSize: 20, fontWeight: 'bold', cursor: 'pointer' },
        drawerList2Container: {
            borderTop: '1px solid #F0F2F6',
            marginLeft: '24px',
            marginRight: 24,
            marginBottom: 40,
        },
        drawerList2: { cursor: 'pointer', fontSize: 16 },
        getStartBtn: {
            backgroundColor: '#FC821F',
            borderRadius: '4px',
            padding: '13px 16px',
            margin: ' 0 auto',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            '&:hover': { cursor: 'pointer' },
            boxSizing: 'border-box',
            marginTop: 14,
        },
        getStartBtnDrawer: {
            backgroundColor: '#FC821F',
            borderRadius: '4px',
            padding: '10px 33px',
            margin: ' 0 auto',
            border: 'none',
            color: 'white',
            fontSize: '20px',
            '&:hover': { cursor: 'pointer' },
            boxSizing: 'border-box',
        },
        loginBtnDrawer: {
            background: 'rgba(0, 0, 0, 0.04)',
            borderRadius: '4px',
            padding: '10px 52px',
            margin: ' 0 auto',
            border: 'none',
            fontSize: '20px',
            '&:hover': { cursor: 'pointer' },
            boxSizing: 'border-box',
            [theme.breakpoints.down('xs')]: { marginBottom: '16px' },
        },
        // appLogo: {
        //     width: '45%',
        // },
        searchContainer: {
            position: 'relative',
            borderRadius: theme.shape.borderRadius,
            backgroundColor: 'rgba(240, 242, 246, 1)',
            '&:hover': {
                backgroundColor: 'rgba(240, 242, 246, 1)',
            },
            marginLeft: 0,
            width: '100%',
            minWidth: 185,
            height: 50,
            [theme.breakpoints.up('sm')]: {
                marginLeft: 25,
                // width: 'auto',
            },
            marginRight: 10,
            [theme.breakpoints.up('xl')]: { marginLeft: 8 },
        },
        // searchIcon: {
        //     padding: theme.spacing(0, 2),
        //     height: '100%',
        //     position: 'absolute',
        //     pointerEvents: 'none',
        //     display: 'flex',
        //     alignItems: 'center',
        //     justifyContent: 'center',
        //     color: 'rgba(155, 158, 156, 1)',
        // },
        searchIcon: {
            // padding: theme.spacing(0, 2),
            // height: '100%',
            // position: 'absolute',
            // pointerEvents: 'none',
            // display: 'flex',
            // alignItems: 'center',
            // justifyContent: 'center',
            marginTop: 8,
            marginLeft: 10,
            color: 'rgba(155, 158, 156, 1)',
        },
        inputRoot: {
            color: 'inherit',
        },
        inputInput: {
            padding: '16px 8px 8px 8px',
            // vertical padding + font size from searchIcon
            // paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
            transition: theme.transitions.create('width'),
            width: '100%',
            // [theme.breakpoints.down('xl')]: {
            //     width: '140px',
            // },
            // [theme.breakpoints.down('lg')]: {
            //     width: '100px',
            // },
            // [theme.breakpoints.down('md')]: {
            //     width: '140px',
            // },
            // [theme.breakpoints.down('sm')]: {
            //     width: '100%',
            // },
        },
        topNavContent2Ul: {
            listStyleType: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        topNavContent2UlLi: {
            fontSize: 16,
        },
        appBar: {
            top: 0,
            left: 0,
            width: 1,
            background: '#FAFAFC',
            boxShadow: 'none',
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
        },
        appBarShift: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: drawerWidth,
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
        menuButton: {
            marginRight: theme.spacing(2),
            color: '#111E14',
        },
        hide: {
            display: 'none',
        },
        drawer: {
            width: drawerWidth,
            flexShrink: 0,
        },
        // drawerPaper: {
        //     width: drawerWidth,
        //     marginTop: 78,
        //     background: 'white',
        //     borderTop: '1px solid #F0F2F6',
        //     overflowY: 'auto',
        //     height: '90%',
        //     '&::-webkit-scrollbar-track': {
        //         borderRadius: '10px',
        //         background: 'none',
        //     },
        //     '&::-webkit-scrollbar': {
        //         width: '8px',
        //         backgroundColor: '#F5F5F5',
        //     },
        //     '&::-webkit-scrollbar-thumb': {
        //         borderRadius: '10px',
        //         backgroundColor: 'rgba(0,0,0,0.2)',
        //     },
        // },
        drawerPaper: {
            width: drawerWidth,
            marginTop: 78,
            background: 'white',
            borderTop: '1px solid #F0F2F6',
        },
        drawerHeader: {
            display: 'flex',
            alignItems: 'center',
            padding: theme.spacing(0, 1),
            // necessary for content to be below app bar
            ...theme.mixins.toolbar,
            justifyContent: 'flex-end',
        },
        content: {
            flexGrow: 1,
            padding: theme.spacing(3),
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            marginLeft: -drawerWidth,
        },
        contentShift: {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        },
        homeLogo: {
            width: 32,
            height: 32,
        },
        drawerButtonContent1: {
            marginRight: '20px',
            marginLeft: 24,
            [theme.breakpoints.down('xs')]: { marginRight: '0px' },
        },
        drawerIconsContainer: { marginTop: 30, marginBottom: 40 },
        drawerIconContent: {
            marginRight: '24px',
            cursor: 'pointer',
        },

        closeBarIcon: {
            marginRight: 19,
            color: 'rgba(17, 30, 20, 1)',
            cursor: 'pointer',
        },
        topNavSectionContent: {},
        jobCenterBtnBox: {
            marginLeft: '24px',
            [theme.breakpoints.down('lg')]: { marginLeft: '18px' },
        },
        homeButton: {
            width: 32,
            marginRight: '10px',
            [theme.breakpoints.down('md')]: { marginRight: '0px' },
        },
        drawerButtonContainer: {},
        buttonContainerModal: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '50px 0', marginBottom: '100px' },
        modalStyle1: {
            position: 'absolute',
            top: '10%',
            left: '10%',
            overflow: 'scroll',
            height: '100%',
            display: 'block',
        },
    }));
};

const useEmployerAccountStyles = (params = {}) => {
    const { isOtherEmployerAccountsExists } = params;

    const employerListContainerBottomBorderStyle = isOtherEmployerAccountsExists ? { borderBottom: '1px solid rgba(17, 30, 20, 0.1)' } : { borderBottom: 0 };

    return makeStyles(theme => ({
        employerListContainer: { margin: '24px 0 10px 0', padding: 0, borderTop: '1px solid rgba(17, 30, 20, 0.1)', ...employerListContainerBottomBorderStyle },
        otherAccountsLabelContainer: { display: 'flex', padding: '5px 32px', borderBottom: '1px solid rgba(17, 30, 20, 0.1)' },
        otherAccountsLabelText: { color: 'rgba(17, 30, 20, 0.6)' },
        newCompanyIcon: { fontSize: 40, color: 'rgba(216, 216, 216, 1)' },
        employerContainer: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', padding: '16px 32px', '&:hover': { backgroundColor: '#fafafa' } },
        addNewEmployerContainer: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '16px 32px',
            '&:hover': { backgroundColor: '#fafafa' },
        },
        newCompanyContainer: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            padding: '16px 32px',
            '&:hover': { backgroundColor: '#fafafa' },
        },
        companyProfilePic: {
            width: 40,
            height: 40,
            border: '1px solid #D5D7DD',
            background: 'rgba(252, 130, 31, 0.1)',
            color: 'rgb(252,130,31)',
            fontSize: 16,
        },
        companyNameText: { marginLeft: 16 },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        // props,
        classes,
        appContext,
        openInviteCount,
        handleClickLogo,
        handleClickDrawerLink,
        handleClickDrawerDashboardLink,
        handleClickDrawerPrivateLink,
        handleClickProfilePic,
        // handleClickPopoverProfilePic,
        handleClickDrawerPrivateLinkBrandPage,

        currentPath,
    } = params;

    const { userId, employerId } = appContext;

    const members = _get(appContext, 'currentEmployer.members', []);
    const member = _find(members, o => o.userId === userId);
    const profilePicUrl = _get(member, 'profilePicUrl');
    // const isAdmin = _get(member, 'isAdmin');

    const defns = {
        topNavContainer: { container: true },
        topNavLeft: { item: true, lg: 3, md: 3, sm: 4, xs: 4 },
        topNavRight: { item: true, lg: 7, md: 7, sm: 5, xs: 5 },
        popOverNavContainer: { item: true, lg: 2, md: 2, sm: 3, xs: 3 },
        topNavBottomRight: { item: true, lg: 7, md: 12, sm: 12, xs: 12 },
        // appLogoContainer: { onClick: handleClickLogo },
        // appLogo: { onClick: handleClickLogo },
        appLogoHomeLink: { onClick: handleClickLogo },
        isAdminText: { variant: 'body1' },
        brandButton: {
            onClick: handleClickDrawerPrivateLinkBrandPage.bind(null, `${PATH_BRAND}/${PATH_SUB_PAGE_EMP}/${employerId}`),

            label: 'My Brand Page',
        },
        // dashboardButton: {
        //     onClick: handleClickDrawerPrivateLink.bind(null, PATH_DASHBOARD),
        //     label: 'My Dashboard',
        // },
        advertiseButton: {
            onClick: handleClickDrawerPrivateLink.bind(null, `${PATH_ADVERTISE}/${ADVERTISE_PAGE_ID_INTRO}`),
            label: 'Advertise With Us',
            isSelected: currentPath === 'advertise' ? true : false,
        },
        openInviteBadge: {
            color: 'primary',
            variant: 'dot',
            invisible: openInviteCount <= 0,
            // overlap: 'circle',
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
        },
        avatar: {
            photoSize: 40,
            borderSize: 3,
            // placeholderFontSize: 15,
            src: profilePicUrl,
            onClick: handleClickProfilePic,
        },
        avatarPopover: {
            photoSize: 90,
            borderSize: 4,
            placeholderFontSize: 30,
            src: profilePicUrl,
            // onClick: handleClickPopoverProfilePic,
        },
        jobCenterLink: {
            onClick: handleClickDrawerPrivateLink.bind(null, PATH_JOB_CENTER),
        },
        resourceCenterLink: {
            onClick: handleClickDrawerPrivateLink.bind(null, PATH_RESOURCE_CENTER),
        },
        employersLink: {
            onClick: handleClickDrawerLink.bind(null, `${PATH_HOME}/${HOMEPAGE_EMPLOYERS}`),
        },
        aboutLink: {
            onClick: handleClickDrawerLink.bind(null, `${PATH_HOME}/${HOMEPAGE_ABOUT}`),
        },
        contactUsLink: {
            onClick: handleClickDrawerLink.bind(null, `${PATH_HOME}/${HOMEPAGE_CONTACT}`),
        },
        FAQLink: {
            onClick: handleClickDrawerLink.bind(null, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_FAQ}`),
        },
        termsOfUseLink: {
            onClick: handleClickDrawerLink.bind(null, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_TERMS}`),
        },
        privacyLink: {
            onClick: handleClickDrawerLink.bind(null, `${PATH_HOME}/${HOMEPAGE_FAQ}/${PATH_SUB_PAGE_PRIVACY}`),
        },
        loginButton: {
            // onClick: handleClickButton.bind(null, PATH_LOGIN),
            label: 'Log In',
        },
        getStartedButton: {
            color: 'primary',
            // onClick: handleClickButton.bind(null, PATH_RESOURCE_CENTER),
            label: 'Get Started',
        },
        drawerButtonContainer: { container: true, direction: 'row', justify: 'flex-start' },
        drawerButtonContent1: { item: true },
        drawerButtonContent2: { item: true },
        drawerIconsContainer: { container: true, direction: 'row', justify: 'center' },
        drawerIconContent: { item: true },
        mySettingLink: { onClick: handleClickDrawerDashboardLink.bind(null, DASHBOARD_ID_MY_SETTINGS) },
        myCalendarLink: { onClick: handleClickDrawerDashboardLink.bind(null, DASHBOARD_ID_MY_CALENDAR) },
        myJobLink: { onClick: handleClickDrawerDashboardLink.bind(null, DASHBOARD_ID_MY_JOBS) },
        myMessagesLink: { onClick: handleClickDrawerDashboardLink.bind(null, DASHBOARD_ID_MY_MESSAGES) },
        myProfileLink: { onClick: handleClickDrawerDashboardLink.bind(null, DASHBOARD_ID_MY_PROFILE) },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const EmployerAccount = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { currentUser } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { logOut, setAvatarPopoverAnchorEl } = layoutContext;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [confirmAddNewCompanyDialogOpen, setConfirmAddNewCompanyDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { idx, employerData = {}, onClick } = props;
    const status = _get(employerData, 'status');

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isAccountSwitchable = getIsAccountSwitchable({ idx, status });
    const email = currentUser?.email;

    // if status is PAS, assume it is user's own company and has not been set up yet
    const isPendingSetup = status === ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP;

    // if status is PEV, user's own company should not be displayed yet
    const isPendingEmailVerification = status === ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickEmployer = useCallback(() => {
        if (onClick) {
            setAvatarPopoverAnchorEl(null);
            onClick(idx);
        }
    }, [idx, setAvatarPopoverAnchorEl, onClick]);

    const handleClickAddNewCompany = useCallback(() => {
        setConfirmAddNewCompanyDialogOpen(true);
    }, []);

    const handleConfirmAddNewCompany = useCallback(() => {
        handleClickEmployer();
    }, [handleClickEmployer]);

    const handleCloseConfirmAddNewCompanyDialog = useCallback(() => {
        setConfirmAddNewCompanyDialogOpen(false);
    }, []);

    const handleLogOut = useCallback(() => {
        logOut && logOut();
    }, [logOut]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useEmployerAccountStyles()();
    const defns = {
        employerContainer: { onClick: handleClickEmployer, disabled: !isAccountSwitchable },
        newCompanyIcon: {},
        addNewEmployerContainer: { onClick: handleClickAddNewCompany, disabled: !isAccountSwitchable },
        confirmAddNewCompanyDialog: {
            email,
            open: confirmAddNewCompanyDialogOpen,
            onClose: handleCloseConfirmAddNewCompanyDialog,
            onConfirm: handleConfirmAddNewCompany,
            onLogOut: handleLogOut,
        },
    };
    setClassNameInDefns(defns, classes);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return !isAccountSwitchable || isPendingEmailVerification ? null : isPendingSetup ? (
        <>
            <ButtonBase {...defns.addNewEmployerContainer}>
                <BusinessIcon {...defns.newCompanyIcon} />
                <AfitdTypography {...defns.companyNameText} value={ADD_NEW_COMPANY} />
            </ButtonBase>
            <ConfirmAddNewCompanyDialog {...defns.confirmAddNewCompanyDialog} />
        </>
    ) : (
        <ButtonBase {...defns.employerContainer}>
            <AfitdAvatar {...defns.companyProfilePic} variant="rounded" src={employerData.logoUrl}>
                ?
            </AfitdAvatar>
            <AfitdTypography {...defns.companyNameText} value={employerData.companyName || COMPANY} />
        </ButtonBase>
    );
};

const OpenInvitations = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    // const appContext = useContext(AppContext);
    // const { currentUser } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setAvatarPopoverAnchorEl } = layoutContext;

    const history = useHistory();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { count = 0 } = props;

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClick = useCallback(() => {
        setAvatarPopoverAnchorEl(null);
        history.push(`${BASE_URL}${PATH_EMP_ACCOUNT_LINKAGE}`);
    }, [setAvatarPopoverAnchorEl, history]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useEmployerAccountStyles()();
    if (count <= 0) return null;
    const defns = {
        openInviteCount: {
            color: 'primary',
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            badgeContent: count,
            max: 99,
        },
        employerContainer: { onClick: handleClick },
    };
    setClassNameInDefns(defns, classes);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <ButtonBase {...defns.employerContainer}>
            <AfitdBadge {...defns.openInviteCount}>
                <AfitdAvatar {...defns.companyProfilePic} variant="rounded">
                    ?
                </AfitdAvatar>
            </AfitdBadge>
            <AfitdTypography {...defns.companyNameText}>{PENDING_EMPLOYER_INVITATIONS}</AfitdTypography>
        </ButtonBase>
    );
};

const EmployerAccountList = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { currentUser, currentEmployerIdx = 0, setCurrentEmployerIdx } = appContext;
    const openInviteCount = _get(currentUser, 'openInviteCount', 0);
    const employers = _get(currentUser, 'employers', []);

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickEmployerAccount = useCallback(
        idx => {
            setCurrentEmployerIdx(idx);
        },
        [setCurrentEmployerIdx],
    );

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const isOtherEmployerAccountsExists =
        _some(employers, (employerData, idx) => {
            const status = _get(employerData, 'status');
            const isAccountSwitchable = getIsAccountSwitchable({ idx, status });

            // if status is PEV, user's own company should not be displayed yet
            const isPendingEmailVerification = status === ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION;

            return idx !== currentEmployerIdx && isAccountSwitchable && !isPendingEmailVerification;
        }) || openInviteCount > 0;

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useEmployerAccountStyles({ isOtherEmployerAccountsExists })();
    const defns = {
        otherAccountsLabelText: { variant: 'body2', component: 'span' },
    };
    setClassNameInDefns(defns, classes);

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.employerListContainer}>
            {isOtherEmployerAccountsExists && (
                <div {...defns.otherAccountsLabelContainer}>
                    <AfitdTypography {...defns.otherAccountsLabelText}>{OTHER_LINKED_COMPANIES}</AfitdTypography>
                </div>
            )}
            {_map(employers, (employer, idx) => {
                return idx === currentEmployerIdx ? null : <EmployerAccount idx={idx} onClick={handleClickEmployerAccount} employerData={employer} />;
            })}
            <OpenInvitations count={openInviteCount} />
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const TopNavEmp = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { currentUser, currentEmployer, emailVerified, pendingAccountSetup, setPendingAccountSetupMessageDialogOpen } = appContext;
    const currentCompanyName = _get(currentEmployer, 'companyName', '');
    const currentTier = _get(currentEmployer, 'tier');
    const openInviteCount = _get(currentUser, 'openInviteCount', 0);

    const layoutContext = useContext(LayoutContext);
    //TODO Move currentThreadId from DashboardLayout to PageBase?
    const { setCurrentDashboard, dashboardData, dashboardDataEmpMember, currentThreadId } = layoutContext;
    const history = useHistory();
    const location = useLocation();
    const currentPath = location.pathname.slice(1, 10);

    // temp switch user
    const { tempSwitchUser } = appContext;
    const { userId } = appContext;
    const members = _get(appContext, 'currentEmployer.members', []);
    const member = _find(members, o => o.userId === userId);
    const isAdmin = _get(member, 'isAdmin');
    const email = _get(member, 'email');
    const fullName = getFullNameMiddleInitialByUser(member);
    // END OF temp switch user

    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const xlMatchesUp = useMediaQuery(theme.breakpoints.up('xl'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [tempAnchorEl, setTempAnchorEl] = useState();
    const [tempMenuHidden, setTempMenuHidden] = useState(false);
    // const [totalUnreadNotificationsCount, setTotalUnreadNotificationsCount] = useState(0);

    // pop over section state and handler
    const [anchorEl, setAvatarPopoverAnchorEl] = React.useState(null);
    const handleCloseProfilePopOver = () => {
        setAvatarPopoverAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    // drawer state and handler
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const handleDrawerOpen = () => {
        setOpenDrawer(true);
    };

    const handleDrawerClose = () => {
        setOpenDrawer(false);
    };

    const logOut = () => {
        handleCloseProfilePopOver();
        appContext.setIsAuthenticationPending(true);
        appContext.auth.signOut();
        if (appContext?.setCurrentUser) {
            appContext.setCurrentUser(null);
        }
        setAvatarPopoverAnchorEl(null);
        history.push(`${BASE_URL}${PATH_HOME}`, { isPromptLogin: true });
    };

    // login or register modal
    // const [loginDialogOpen, setLoginDialogOpen] = useState(false);
    // console.log(`for avoiding eslint warning ${loginDialogOpen} , please remove it if you don't need it`);

    // const [isLoginMode, setIsLoginMode] = useState(false);
    // console.log(`for avoiding eslint warning ${isLoginMode} , please remove it if you don't need it`);

    // const handleOpenLoginModal = () => {
    //     setLoginDialogOpen(true);
    // };
    // const handleCloseLoginDialog = () => {
    //     setLoginDialogOpen(false);
    // };

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const preAccountSetup = !emailVerified || pendingAccountSetup;
    const manageAccountBtnIsHidden = preAccountSetup;

    // Calculating Total unread notifications count
    const actionNeededPendingInterviews = _get(dashboardData, 'actionNeededPendingInterviews'); // array
    const unreadDeclinedInterviews = _get(dashboardData, 'unreadDeclinedInterviews'); // array
    const unreadMsgsCounts = getUnreadMessageCountByDashboardData({ dashboardData, dashboardDataEmpMember, currentThreadId });

    const totalUnreadNotificationsCount = unreadMsgsCounts + actionNeededPendingInterviews?.length + unreadDeclinedInterviews?.length;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // ? closing Drawer if switch between mobile/Tab to desktop view
    useEffect(() => {
        if (!mdMatches && !smMatches) setOpenDrawer(false);
    }, [smMatches, mdMatches]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickLogo = useCallback(() => {
        history.push(`${BASE_URL}${PATH_HOME}`);
    }, [history]);

    const handleClickDrawerLink = useCallback(
        path => {
            history.push(`${BASE_URL}${path}`);
        },
        [history],
    );

    const handleClickDrawerDashboardLink = useCallback(
        (dashboardId = 1) => {
            if (preAccountSetup) {
                setPendingAccountSetupMessageDialogOpen(true);
            }
            if (setCurrentDashboard) {
                setCurrentDashboard(dashboardId);
            } else {
                history.push(`${BASE_URL}${PATH_DASHBOARD}/${dashboardId}`);
            }
        },
        [history, preAccountSetup, setPendingAccountSetupMessageDialogOpen, setCurrentDashboard],
    );

    const handleClickDrawerPrivateLink = useCallback(
        path => {
            if (preAccountSetup) {
                setPendingAccountSetupMessageDialogOpen(true);
            }
            history.push(`${BASE_URL}${path}`);
        },
        [history, preAccountSetup, setPendingAccountSetupMessageDialogOpen],
    );
    const handleClickDrawerPrivateLinkBrandPage = useCallback(
        path => {
            if (preAccountSetup) {
                setPendingAccountSetupMessageDialogOpen(true);
            }
            const win = window.open(`${BASE_URL}${path}`, '_blank');
            win.focus();
            // history.push(`${BASE_URL}${path}`);
        },
        [preAccountSetup, setPendingAccountSetupMessageDialogOpen],
    );

    const handleClickProfilePic = useCallback(evt => {
        setAvatarPopoverAnchorEl(evt.currentTarget);
    }, []);

    const handleClickPopoverProfilePic = useCallback(evt => {
        setAvatarPopoverAnchorEl(evt.currentTarget);
    }, []);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ currentTier })();
    if (props.isHidden) return null;
    const defns = getDefns({
        props,
        classes,
        appContext,
        openInviteCount,
        handleClickLogo,
        handleClickDrawerLink,
        handleClickDrawerDashboardLink,
        handleClickDrawerPrivateLink,
        handleClickProfilePic,
        handleClickPopoverProfilePic,
        handleClickDrawerPrivateLinkBrandPage,

        currentPath,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...layoutContext, logOut, setAvatarPopoverAnchorEl }}>
            <Grid {...defns.topNavContainer}>
                <Grid {...defns.topNavLeft}>
                    {mdMatches ? null : (
                        <AfitdBox {...defns.appLogoContainer}>
                            <span {...defns.appLogoHomeLink}>
                                <Logo {...defns.appLogo} />
                            </span>
                        </AfitdBox>
                    )}
                    {mdMatches ? (
                        openDrawer ? (
                            /* ------------------ 0 - 1023 (drawer opened) ------------------ */
                            <Grid {...defns.logoDrawerContainer}>
                                <div {...defns.drawerIconContainer}>
                                    <CloseIcon onClick={handleDrawerClose} {...defns.menuBarIcon} />
                                </div>

                                <div {...defns.appLogoHomeLink}>
                                    <LogoSm {...defns.appLogo} />
                                </div>
                            </Grid>
                        ) : (
                            /* ------------------ 0 - 1023 (drawer closed) ------------------ */
                            <Grid {...defns.logoDrawerContainer}>
                                <div {...defns.drawerIconContainer}>
                                    <MenuIcon onClick={handleDrawerOpen} {...defns.menuBarIcon} />
                                </div>

                                <div {...defns.appLogoHomeLink}>
                                    <LogoSm {...defns.appLogo} />
                                </div>
                            </Grid>
                        )
                    ) : null}
                </Grid>

                {/* {smMatches ? null : ( */}
                <Grid {...defns.topNavRight}>
                    <div {...defns.topNavRightContainer}>
                        {mdMatches ? null : (
                            <>
                                <AfitdBox {...defns.brandButtonContainer}>
                                    <TopNavButton {...defns.brandButton} />
                                </AfitdBox>
                                {smMatches ? null : <EmpDashboardPopover />}
                            </>
                        )}

                        {/* <AfitdBox {...defns.advertiseButtonContainer}>
                            <TopNavButton {...defns.advertiseButton} />
                        </AfitdBox> */}

                        {/* ------------------ Temporary Switch User button ------------------ */}

                        {xlMatchesUp && process.env.REACT_APP_ENV !== 'PROD' && !tempMenuHidden && (
                            <div {...defns.tempMenuButtonContainer}>
                                <TopNavButton
                                    onClick={evt => {
                                        setTempAnchorEl(evt.currentTarget);
                                    }}
                                    label="TEMP"
                                />
                                <Menu
                                    id="simple-menu"
                                    anchorEl={tempAnchorEl}
                                    keepMounted
                                    open={Boolean(tempAnchorEl)}
                                    onClose={() => {
                                        setTempAnchorEl(null);
                                    }}
                                >
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('entero_it_solutions');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Entero IT Solutions (Tier 3)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('dairy_queen_1');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Dairy Queen (Tier 1)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('coaching_prospects_2');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Coaching Prospects (Tier 2)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('american_painting_2');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to American Painting (Tier 2)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('s4_construction_3');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to S4 Construction (Tier 3)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('tesla_3');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Tesla (Tier 3)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('chicago_bulls');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Chicago Bulls (Tier 3)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('chipotle');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Chipotle (Tier 2)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('six_flags_america');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Six Flags America (Tier 3)
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('michael_jordan');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Michael Jordan
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('tom_clancy');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Tom Clancy
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('john_ramsey');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to John Ramsey
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('tina_nickel');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Tina Nickel
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('sheri_smith');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Sheri Smith
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('shelly_knolls');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Shelly Knolls
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('horace_grant');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Horace Grant
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('scottie_pippen');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Scottie Pippen
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('dennis_rodman');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Dennis Rodman
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('steve_kerr');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Steve Kerr
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            tempSwitchUser('dawn_small');
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Switch to Dawn Small
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            history.push(`${BASE_URL}${PATH_SURVEY}/HUSeSR9Io3Yl8nBmjBA4`);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Survey JS
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            history.push(`${BASE_URL}${PATH_SURVEY}/TmjIzPuABf`);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Survey EMP
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            history.push(`${BASE_URL}${PATH_JOIN_INTERVIEW}/${PATH_JOIN_INTERVIEW_TEST_JOIN_CODE}`);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Join Interview
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            history.push(`${BASE_URL}${PRE_LAUNCH_PAGE}`);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Pre-Launch Page
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            history.push(`${BASE_URL}${PATH_ADVERTISE}/intro`);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Advertise With Us
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            history.push(`${BASE_URL}${PATH_ADMIN_CONSOLE}/${ADMIN_ID_JOB_SEEKER}`);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Admin Console
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setTempMenuHidden(true);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Hide TEMP Menu
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            history.push(`${BASE_URL}/quick-admin/owSoenQPvcne888`);
                                            setTempAnchorEl(null);
                                        }}
                                    >
                                        Temp Admin
                                    </MenuItem>
                                </Menu>
                            </div>
                        )}

                        {/* ------------------ END OF Temporary Switch User button ------------------ */}
                    </div>
                </Grid>
                {/* )} */}
                <Grid {...defns.popOverNavContainer}>
                    <AfitdBox {...defns.avatarContainer}>
                        {/* notification badge section */}
                        {mdMatches && totalUnreadNotificationsCount > 0 && (
                            <div {...defns.notificationBadgeContainer} onClick={handleDrawerOpen}>
                                <p {...defns.notificationBadge}>{totalUnreadNotificationsCount}</p>
                            </div>
                        )}
                        <AfitdBadge {...defns.openInviteBadge}>
                            <AfitdAvatarWithRing {...defns.avatar} />
                        </AfitdBadge>
                        <Popover
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleCloseProfilePopOver}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <div {...defns.profilePopOverContainer}>
                                <div {...defns.closeIconContainerProfile}>
                                    <CloseIcon {...defns.closeIconContent} onClick={handleCloseProfilePopOver} />
                                </div>
                                <div {...defns.profileImage}>
                                    <AfitdAvatarWithRing {...defns.avatarPopover} />
                                </div>
                                <div {...defns.popoverContents}>
                                    <p {...defns.fullNameText}>{fullName} </p>
                                    {/* <AfitdCompanyName {...defns.currentCompanyNameText} companyName={currentCompanyName} isEmpBadge={currentTier === 3} isCenterText={true} /> */}
                                    <AfitdTypography {...defns.currentCompanyNameText}>
                                        {currentCompanyName}{' '}
                                        <span {...defns.verifyIconContainer}>
                                            <VerifiedUserRoundedIcon {...defns.verifyIcon} />
                                        </span>
                                    </AfitdTypography>
                                    <AfitdTypography {...defns.isAdminText}>{isAdmin ? 'Administrator (You)' : 'Team Member (You)'}</AfitdTypography>
                                    <p {...defns.emailText}>{email} </p>
                                    <AfitdButton
                                        {...defns.manageAccountBtn}
                                        variant="containedSecondary"
                                        onClick={() => {
                                            handleCloseProfilePopOver();
                                            history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_SETTINGS}/${DASHBOARD_ID_MY_SETTINGS_USERS}`);
                                            setAvatarPopoverAnchorEl(null);
                                        }}
                                        isHidden={manageAccountBtnIsHidden}
                                    >
                                        Manage your Account
                                    </AfitdButton>
                                    {!manageAccountBtnIsHidden && <br />}

                                    <EmployerAccountList />

                                    <AfitdButton
                                        {...defns.logOutBtn}
                                        variant="contained"
                                        onClick={() => {
                                            handleCloseProfilePopOver();
                                            appContext.setIsAuthenticationPending(true);
                                            appContext.auth.signOut();
                                            if (appContext?.setCurrentUser) {
                                                appContext.setCurrentUser(null);
                                            }
                                            setAvatarPopoverAnchorEl(null);
                                            history.push(`${BASE_URL}${PATH_HOME}`, { isPromptLogin: true });
                                        }}
                                    >
                                        Log Out
                                    </AfitdButton>
                                    <p {...defns.privacyAndTermsText}>
                                        <span {...defns.privacyLink}>Privacy Policy</span> / <span {...defns.termsOfUseLink}>Terms of Use</span>
                                    </p>
                                </div>
                            </div>
                        </Popover>
                    </AfitdBox>
                </Grid>
                {/* for sm and xm bottom navbar */}
                {/* {smMatches ? () : null} */}
                {/* ================== Drawer Section ================== */}
                {/* ------------------ below 1023 ------------------ */}
                {/* {mdMatches && (
                <div className={classes.root}>
                    <CssBaseline />
                    <AppBar position="fixed"></AppBar>
                    <Drawer
                        className={classes.drawer}
                        variant="persistent"
                        anchor="left"
                        open={openDrawer}
                        classes={{
                            paper: classes.drawerPaper,
                        }}
                    >
                        <List {...defns.drawerListContainer}>
                            <p {...defns.myCalendarLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>My Calendar</span>
                            </p>
                            <p {...defns.myJobLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>My Jobs</span>
                            </p>
                            <p {...defns.myMessagesLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>My Messages</span>
                            </p>
                            <p {...defns.myProfileLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>My Profile</span>
                            </p>
                            <p {...defns.jobCenterLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>Job Center</span>
                            </p>
                            <p {...defns.resourceCenterLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>Resource Center</span>
                            </p>
                            <p {...defns.mySettingLink} {...defns.drawerList1}>
                                <span onClick={handleDrawerClose}>My Settings</span>
                            </p>
                        </List>
                        <List {...defns.drawerList2Container}>
                            <p {...defns.aboutLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>About Us</span>
                            </p>
                            <p {...defns.termsOfUseLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>Terms of Use</span>
                            </p>
                            <p {...defns.FAQLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>Frequently Asked Questions</span>
                            </p>
                            <p {...defns.privacyLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>Privacy Policy</span>
                            </p>
                            <p {...defns.contactUsLink} {...defns.drawerList2}>
                                <span onClick={handleDrawerClose}>Contact Us</span>
                            </p>
                        </List>
                        <Grid {...defns.drawerButtonContainer}>
                            <Grid {...defns.drawerButtonContent1} onClick={handleDrawerClose}>
                                <span onClick={() => setIsLoginMode(true)}>
                                    <span onClick={handleOpenLoginModal}>
                                        <AfitdButton {...defns.loginButton} {...defns.loginBtnDrawer} />
                                    </span>
                                </span>
                            </Grid>
                            <Grid {...defns.drawerButtonContent2} onClick={handleDrawerClose}>
                                <span onClick={handleOpenLoginModal}>
                                    <span onClick={() => setIsLoginMode(false)}>
                                        <AfitdButton {...defns.getStartedButton} {...defns.getStartBtnDrawer} />
                                    </span>
                                </span>
                            </Grid>
                        </Grid>

                        <Grid {...defns.drawerIconsContainer}>
                            <Grid {...defns.drawerIconContent}>
                                <img src={instagram} alt="" />
                            </Grid>
                            <Grid {...defns.drawerIconContent}>
                                <img src={facebook} alt="" />
                            </Grid>
                            <Grid {...defns.drawerIconContent}>
                                <img src={pinterest} alt="" />
                            </Grid>
                            <Grid {...defns.drawerIconContent}>
                                <img src={twitter} alt="" />
                            </Grid>
                        </Grid>
                    </Drawer>
                </div>
            )} */}
                {/* ================== Drawer Section ================== */}
                {/* ------------------ below 768 ------------------ */}
                <div className={classes.root}>
                    <CssBaseline />
                    <AppBar position="fixed"></AppBar>
                    <Drawer
                        className={classes.drawer}
                        variant="persistent"
                        anchor="left"
                        open={openDrawer}
                        classes={{
                            paper: classes.drawerPaper,
                        }}
                    >
                        <TopNavEmpSmallMenu handleDrawerClose={handleDrawerClose} />
                    </Drawer>
                </div>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default TopNavEmp;
